// Sugalarm — public password-reset page (cycle 95).
//
// Unauthenticated SPA route at /reset-password. Reads ?token=, takes a
// new password + confirm, calls POST /api/auth/reset_password, and on
// success routes to /login with a confirmation. Renders on web + mobile;
// not part of the kiosk shell. Self-contained inline styling on the
// theme tokens so it works before any settings/theme context loads.

import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import * as authApi from "../../api/auth";
import { Logo } from "../../components/ui/Logo";

const MIN_LEN = 8;

const wrap: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
  background: "var(--bg)",
  color: "var(--ink)",
  fontFamily: "var(--ui-font)",
};
const card: React.CSSProperties = {
  width: "min(420px, 100%)",
  background: "var(--bg-elev)",
  border: "1px solid var(--line)",
  borderRadius: 16,
  padding: "28px 26px",
};
const label: React.CSSProperties = {
  display: "block",
  fontSize: "var(--type-floor)",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--ink-3)",
  marginBottom: 6,
};
const input: React.CSSProperties = {
  width: "100%",
  font: "inherit",
  padding: "11px 13px",
  borderRadius: 9,
  border: "1px solid var(--line-strong)",
  background: "var(--bg)",
  color: "var(--ink)",
  marginBottom: 14,
  boxSizing: "border-box",
};
const btn: React.CSSProperties = {
  width: "100%",
  font: "inherit",
  fontWeight: 700,
  padding: "12px 16px",
  borderRadius: 10,
  border: "1px solid var(--accent)",
  background: "var(--accent)",
  color: "var(--accent-ink, #fff)",
  cursor: "pointer",
};
const linkBtn: React.CSSProperties = {
  display: "inline-block",
  marginTop: 14,
  color: "var(--accent)",
  background: "none",
  border: "none",
  font: "inherit",
  fontWeight: 600,
  cursor: "pointer",
  padding: 0,
};

// Cycle 453.2: when a signed-in session opens this page it renders INSIDE
// the alarm shell (the frame, its No Data banner and the off-Now alarm
// control), so it fills the space the frame gives it instead of the whole
// viewport. Layout only — the form, its requests and its navigation are
// unchanged.
const fitWrap: React.CSSProperties = {
  ...wrap,
  minHeight: 0,
  flex: "1 1 auto",
  overflowY: "auto",
};

export function ResetPasswordScreen({ fitFrame = false }: { fitFrame?: boolean } = {}): JSX.Element {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") ?? "";

  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (pw.length < MIN_LEN) {
      setError(`Password must be at least ${MIN_LEN} characters.`);
      return;
    }
    if (pw !== confirm) {
      setError("Passwords don’t match.");
      return;
    }
    setBusy(true);
    const res = await authApi.resetPassword({ token, new_password: pw });
    setBusy(false);
    if (res.ok) setDone(true);
    else setError(res.error.message || "Could not reset the password.");
  };

  return (
    <div style={fitFrame ? fitWrap : wrap}>
      <div style={card}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 18,
          }}
        >
          <Logo size={40} />
          <span style={{ fontWeight: 700, fontSize: 18 }}>Sugar Alarm</span>
        </div>

        {!token ? (
          <Invalid
            message="This reset link is missing its token."
            onLogin={() => nav("/login")}
          />
        ) : done ? (
          <div>
            <h2 style={{ margin: "0 0 8px", fontSize: 20 }}>
              Password updated
            </h2>
            <p style={{ color: "var(--ink-2)", lineHeight: 1.5 }}>
              Your password has been reset. You can now sign in with your new
              password.
            </p>
            <button style={btn} onClick={() => nav("/login")}>
              Go to sign in
            </button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h2 style={{ margin: "0 0 4px", fontSize: 20 }}>
              Set a new password
            </h2>
            <p
              style={{
                color: "var(--ink-3)",
                fontSize: 13,
                lineHeight: 1.5,
                margin: "0 0 18px",
              }}
            >
              Choose a new password for your Sugar Alarm account.
            </p>

            <label style={label} htmlFor="pw">
              New password
            </label>
            <input
              id="pw"
              style={input}
              type="password"
              autoComplete="new-password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder={`At least ${MIN_LEN} characters`}
            />
            <label style={label} htmlFor="confirm">
              Confirm password
            </label>
            <input
              id="confirm"
              style={input}
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />

            {error && (
              <div
                style={{
                  color: "var(--zone-urgent)",
                  fontSize: 13,
                  marginBottom: 12,
                }}
              >
                {error}
              </div>
            )}

            <button style={btn} type="submit" disabled={busy}>
              {busy ? "Saving…" : "Reset password"}
            </button>
            <button
              type="button"
              style={linkBtn}
              onClick={() => nav("/login")}
            >
              ← Back to sign in
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Invalid({
  message,
  onLogin,
}: {
  message: string;
  onLogin: () => void;
}): JSX.Element {
  return (
    <div>
      <h2 style={{ margin: "0 0 8px", fontSize: 20 }}>Reset link problem</h2>
      <p style={{ color: "var(--ink-2)", lineHeight: 1.5 }}>{message}</p>
      <button style={btn} onClick={onLogin}>
        Go to sign in
      </button>
    </div>
  );
}
